import {VectorLayer} from "@/engine/layers/vectorLayer.js";
import * as L from "leaflet"
import {CreateDrawingContextMenu} from "@/engine/drawingsContextMenu.js";
import {defaultContext, mapApi, mapContext, shortLivedTokenManager, waiter} from "@/context/global.js";
import {elog} from "@/engine/helpers/logging.js";
import {AMUtil} from "@/engine/leaflet/antimeridianUtils.js";
import {CN_GEOMETRY_OP_CREATE, CN_GEOMETRY_OP_DELETE, CN_GEOMETRY_OP_UPDATE} from "@/engine/comm/sseCM.js";
import {LAYER_TYPE_VECTOR_LINE, LAYER_TYPE_VECTOR_POINT, LAYER_TYPE_VECTOR_POLYGON} from "@/engine/layers/layer.js";
import {LeafletWfstLayerAdapter} from "@/engine/wfst/leafletWfstLayerAdapter.js";

class VectorLayerDrawing extends VectorLayer {

    constructor(struct) {

        const LAYER_DRAWTYPES = {
            [LAYER_TYPE_VECTOR_POLYGON]: ["Rectangle", "Polygon"],
            [LAYER_TYPE_VECTOR_LINE]: ["Line"],
            [LAYER_TYPE_VECTOR_POINT]: ["Circle", "Marker"]
        }

        super(struct)
        this.typeDrawing = true
        this.drawTypes          = LAYER_DRAWTYPES[struct.dataType]
        this._changed           = false;  //if there is geometries we need to save()
        this._deferSave         = false;  //when true, the WFST save() operation is suppressed
        this.tooltipConfiguration = {direction: "top", permanent: true, offset: [-12, -15], opacity: 0.7, className: "tooltip-label"}
        this.styleOptions = ['color', 'weight', 'fillColor', 'fillOpacity', 'dashArray', 'opacity']
        this.featuresCoveredBounds     = null
        this.featuresCount      = 0
        this.wfsRequestHeaders = {}
        this.tokenChangeUnsubscribe = null
        this.authorizationChangeHandler = this._setAuthorizationHeader.bind(this)
    }

    addOnMap(map) {
        this._bindTokenAuthorization()
        super.addOnMap(map, function () {
           return this._createWFSTLayer()
        }.bind(this))
    }

    // calls on deleting the layer
    removeFromMap() {
        this._unbindTokenAuthorization()
        // this should be done before _destroyWFSTLayer in order the leaflet layers 'remove' event handler
        // detected the vectorLayer is removed (see layer.remove and map.zoomend handlers)
        this.map = null
        this._destroyWFSTLayer()
        super.removeFromMap()
    }

    show(byUser = false) {
        super.show(byUser)
        if (this.WFSTLayer) {
            // features loading is disabled at the time the layer was created (case the layer may be hidden)
            // so launch loading features on showing layer
            if (!this.WFSTLayer.options.showExisting) {
                this.WFSTLayer.options.showExisting = true
                this.WFSTLayer.loadFeatures(this.WFSTLayer.options.filter)
            }
        }
    }

    getBounds() {
        return this.featuresCoveredBounds
    }

    //WFS Transactional layer, used for editable geometries
    _createWFSTLayer() {
        if (this.WFSTLayer != null)
            return this.WFSTLayer

        const WFSTLayerConstructor = defaultContext.wfstBackend === "openlayers" ?
            LeafletWfstLayerAdapter :
            window.L.WFST

        this.WFSTLayer = new WFSTLayerConstructor({
            url: this._joinServerUrl("ows"),
            geometryField: 'geom',
            typeNS: this.workspace.id,
            typeName: this.id,
            crs: L.CRS.EPSG4326,
            forceMulti: true, // to force using Multi covering tag for geometries (needs to be compatible with geoserver's wfst)
            headers: this.wfsRequestHeaders,
            showExisting: this.visible,
            style: function (l) {
                try {
                    let options = JSON.parse(l.getProperty('options'))
                    //new way
                    if ("style" in options) {
                        return options.style
                    } else {
                        //old way
                        return options
                    }
                } catch {

                }
            }
        })

        // geoman drawing && style changing handlers
        let evGeometryEditHandlers = function (leafletLayer) {
            //this.debug && this.elog("DEBUG", `hangGeometryEventsHandlers for geometry '${leafletLayer.getProperty('name')}'`)
            leafletLayer.on('pm:update', function (l) {
                this.saveFeature(l.layer)
            }.bind(this));
            leafletLayer.on('pm:edit', function (l) {
                this.debug && this.elog("DEBUG", "pm:edit event")
                //this pm:edit event is fired when a layer is edited by drawing operation or any layer modification operation like Rotation or Cutting
                //we avoid the layer saving on each vertex edit, it will be done on "update"
                //but layer changes like cutting or rotating should be saved here
                if (l.layer.pm.enabled()) //layer is in edit mode, skip saving of vertex changes
                    return

                try {
                    this.saveFeature(l.layer);
                } catch (e) {
                    this.elog("ERROR", "Can't save modified vector shape: " + e.toString());
                    //todo add modification revoking if possible
                    if (sm) {
                        sm.notice("Can't save the shape: internal error, try later");
                    }
                }
            }.bind(this));
            leafletLayer.on('pm:remove', function (l) {
                this.debug && this.elog("DEBUG", "pm:remove event")
                this.removeFeature(l.layer)
            }.bind(this));

            //todo maybe is any there a better location for this init call
            mapContext.mapUI.enableVertexCoordinateEditAbility(leafletLayer)

            //
            mapContext.mapUI.enableGeometryDbClickEditAbility(leafletLayer)

        }.bind(this)

        // control handlers (click, mouseover and etc on vector layers
        let evGeometryControlHandlers = function (leafletLayer) {
            leafletLayer.on('click', function (l) {
                try {
                    //it is for debug purpose only to watch clicked geometries
                    //todo refactor to avoid using global mapContext object
                    mapContext.mapUI.geometryMetrics.set("id", l.target.feature.id);
                    mapContext.mapUI.geometryMetrics.set("workspace", this.workspace.id);
                    mapContext.mapUI.geometryMetrics.set("name", l.target.getProperty("name"));
                    mapContext.mapUI.geometryMetrics.set("boundingBox", l.target.getBounds());
                } catch (e) {

                }
            }.bind(this))
            /*
            //experimental, attempt to show geometry length/square on mouse over
            leafletLayer.on('mouseover', function (l) {
//                    if (typeof l.target.showLength() === "function")
                //                      l.target.showLength();
                //l.target.setText('1000', {repeat: false, bellow: false, offset: 12, center: true, orientation: 'flip', attributes: {fill: 'red'}});
            });

            leafletLayer.on('mouseout', function (l) {
//                    if (typeof l.target.hideLength() === "function")
                //                      l.target.hideLength();
                //l.target.setText(null);
            });
             */
        }.bind(this)


        this.WFSTLayer.on('load', function (e) {
            if (!e.layers) {
                this.debug && this.elog("DEBUG", `Loading vector data is skipped for ${this.workspace.id}:${this.id} cause the layer is hidden`);
                return
            }
            if (e.layers.length > 0) {
                this._logdebug(`Features (${e.layers.length}) is loaded`)
                //this.debug && this.elog("DEBUG", `Features (${e.layers.length}) is loaded for layer ${this.workspace.id}:${this.id}`);
            }

            for (let l of e.layers) {
                l._maphubVectorLayer = this;

                // fix antimeridian issues (see antimeridianUtils)
                if (typeof AMUtil !== 'undefined')
                    AMUtil.fixLayer(l)

                this.featuresCount++

                // GeometryDimension module debugging
                //l._debug = this.debug;

                // set edit handlers on leaflet layers
                if (this.workspace.writable) {
                    //hang geometry edit events handlers
                    evGeometryEditHandlers(l);
                } else {
                    //ignore feature by geoman editor
                    l.options.pmIgnore = true;
                }

                // set handlers for control events on leaflet layer (mouse clicks and etc)
                evGeometryControlHandlers(l);

                // read labels display options and perform
                this._initLabelsDisplayFeature(l)

                //enables labels and dimension inscription feature by GeometryDimension plugin
                if (typeof l.enableLabels === "function") {
                    l.enableLabels();
                }

                // init marker
                if (l instanceof L.Marker) {
                    this._initMarker(l);
                }

                // init context menu
                CreateDrawingContextMenu(l)


                // zoom level visibility control
                // now it is controlled by workspace
                // todo need to adopted to hide element when it becomes too small
                /*
                // init custom behavior on zooming (show/hide layer on certain zoom level)
                let zoomlevelVisibility = function (ev) {
                    this._updateGeometryZoomVisibility(l)
                    // if is called by manual or add on map event,
                    if (! ev || ev.type === 'add') {
                        this.map.on("zoomend", zoomlevelVisibility);
                        this._gdebug(l, "zoomend events has been added")
                    }
                }.bind(this);

                // calls when layer is added after workspace visibility was changed
                l.on("add", zoomlevelVisibility);

                // updates geometry visibility and sets zoomend handler
                zoomlevelVisibility()

                l.on("remove", function (ev) {
                    // remove event handlers when layer is removed
                    // layer could be removed by:
                    //      zoomlevel (hidden at some zoomlevel)
                    //      totally vectorlayer and workspace delete
                    //      hide the workspace
                    // // vectorLayer is totally removed (deleted)
                    if (ev.target._map !== null) {
                        // remove
                        ev.target._map.off("zoomend", zoomlevelVisibility);
                        ev.target.off("add", zoomlevelVisibility);
                        this._gdebug(l, "zoomend and add events has been removed")
                        return
                    }
                    // layer is hidden
                    if (!this.workspace.visible) {
                        ev.target._map.off("zoomend", zoomlevelVisibility);
                        this._gdebug(l, "zoomend event has been removed")
                    }
                }.bind(this))

                 */
            }
            this.updateFeaturesCoveredBounds(e.layers)
        }.bind(this))

        //shape saving errors
        this.WFSTLayer.on('save:failed', function (e) {
            let pubMessage = "Internal server error, try later";
            let intMessage = "unknown error";
            if (e && e.exceptions && e.exceptions.length > 0) {
                //todo need to handle all the errors from array
                intMessage = e.exceptions[0].text;
            }
            if (intMessage.includes("read-only")) {
                pubMessage = "workspace is read-only"
            }
            this.elog("ERROR", "WFST: can't save the geometry: " + intMessage);
            if (sm) {
                sm.noticeMessage("Can't save the geometry: " + pubMessage);
            }
            //remove created geometry we couldn't save
            for (let i in e.sourceTarget.changes) {
                let layer = e.sourceTarget.changes[i];
                if (layer.state && layer.state === e.sourceTarget.state.insert) {
                    layer.remove();
                }
            }
        }.bind(this))

        //shape is inserted successfully
        this.WFSTLayer.on('save:success', function (e) {
            this._sendNotification(CN_GEOMETRY_OP_CREATE, e.layers)
        }.bind(this))

        this.WFSTLayer.on('wfst:update:success', function (e) {
            this.updateFeaturesCoveredBounds(e.layers)
            this._sendNotification(CN_GEOMETRY_OP_UPDATE, e.layers)
        }.bind(this))

        this.WFSTLayer.on('wfst:delete:success', function (e) {
            this.featuresCount--
            this._sendNotification(CN_GEOMETRY_OP_DELETE, e.layers)
        }.bind(this))

        //usually it's wfs load errors
        this.WFSTLayer.on('error', function (e) {
            let intMessage = "unknown error";
            if (e.error && e.error.message) {
                intMessage = e.error.message
            }
            this.elog("ERROR", `can't load vector layer ${e.target.options.typeNSName}: ${intMessage}`);
/*
            if (sm) {
                sm.serverError();
            }
*/
        }.bind(this))

        return this.WFSTLayer
    }

    /**
     * Initializes the current Authorization value and subscribes to token changes.
     * Repeated calls preserve the existing subscription.
     * @returns {void}
     * @private
     */
    _bindTokenAuthorization() {
        this._setAuthorizationHeader(shortLivedTokenManager.getAuthorizationHeader())
        if (this.tokenChangeUnsubscribe == null) {
            this.tokenChangeUnsubscribe = shortLivedTokenManager.onChange(this.authorizationChangeHandler)
        }
    }

    /**
     * Releases the token-change subscription and removes the transport credential.
     * @returns {void}
     * @private
     */
    _unbindTokenAuthorization() {
        if (this.tokenChangeUnsubscribe != null) {
            this.tokenChangeUnsubscribe()
            this.tokenChangeUnsubscribe = null
        }
        this._setAuthorizationHeader(null)
    }

    /**
     * Updates the reusable WFS request headers with a prepared Authorization value.
     * @param {string|null} authorizationHeader Prepared Bearer value or null.
     * @returns {void}
     * @private
     */
    _setAuthorizationHeader(authorizationHeader) {
        if (authorizationHeader == null) {
            delete this.wfsRequestHeaders.Authorization
            return
        }
        this.wfsRequestHeaders.Authorization = authorizationHeader
    }

    _destroyWFSTLayer() {
        this.WFSTLayer.eachLayer(function (l) {
            l.off('pm:update')
            l.off('pm:edit')
            l.off('pm:remove')
            l.off('click')
            l.off('mouseover')
            l.off('mouseout')
            l.off("remove")
            // todo rewrite zoomend event handler
            // todo possible need to clear geometry dimension events handlers
        }, this)
        this.WFSTLayer.off()
    }

    /**
     * adds a created geometry to layer
     * usually calls from goeman pm:create event to add layer to WFST layer
     * @param layer
     * @param properties
     */
    addFeature(layer, properties) {
        if (! this.WFSTLayer) {
            elog("ERROR", `WFST layer object is undefined for layer ${this.id}`)
            layer.remove();
            if (sm) {
                sm.noticeMessage("Can't save vector data: internal error, try later");
            }
            return false;
        }

        let newOneLayer = layer.feature === undefined
        this.WFSTLayer.addLayer(layer);
        if (newOneLayer)
            layer.setProperties(this.defaultProperties);
        if (properties !== undefined) {
            layer.setProperties(properties);
            // init style options
            this._initStyle(layer)
            // init labels display options
            this._initLabelsDisplayFeature(layer)
        }

        let done = false;
        try {
            this.saveFeature(layer);
            done = true;
        } catch (e) {
            this.elog("ERROR", "Can't save a new vector shape: " + e.toString());
            layer.remove();
            if (sm) {
                sm.noticeMessage("Can't save vector data: internal error, try later");
            }
        }
        return done;
    }

    addFeatures(llayers) {
        this.setCommitMode(true)
        for (let i in llayers) {
            this.addFeature(llayers[i])
        }
        this.setCommitMode(false)
    }

    /**
     * _saveVectorLayer saves leafletLayer using WFS
     * @param layer
     * @private
     */

    saveFeature(layer) {
        //this.debug && this.elog("DEBUG", "saveVectorLayer")
        layer.setProperties({ options: JSON.stringify(this._WFSTFeatureOptions(layer))})
        this.WFSTLayer.editLayer(layer);
        if ( this._deferSave)  {
            this._changed = true;
            return
        }

        this._changed = false;
        this.WFSTLayer.save();
    }

    removeFeature(llayer) {
        this.WFSTLayer.removeLayer(llayer);
        if ( this._deferSave)  {
            this._changed = true;
        } else {
            this.WFSTLayer.save();
        }
    }

    removeFeatures(llayers, onFinish) {
        let rLayers = []
        this.setCommitMode(true)
        let wfst = this.WFSTLayer
        let countRemovedLayers = function (e) {
            for (let l of llayers) {
                for (let rLayer of e.layers) {
                    if (rLayer === l) {
                        // cleanup wfst state variable in order this layer could be operated with another wfst
                        rLayer.state = null
                        rLayers.push(rLayer)
                    }
                }
            }
            if (rLayers.length === llayers.length) {
                wfst.off('wfst:delete:success', countRemovedLayers)
                if (onFinish)
                    onFinish(rLayers)
            }
        }

        // catch remove event and calls onFinish when all layers were really removed
        // event handlers is set for one execution
        // we rely on a wfst is done this operation at once
        wfst.once('wfst:delete:success', countRemovedLayers)
        for (let l of llayers) {
            this.removeFeature(l, false)
        }
        this.setCommitMode(false)
    }
    /**
     * sets the mode where geometries saving is deffer
     * used to avoid massive concurrent saving transactions because a lot of geometries were changed at once
     * @param yes - boolean
     */
    setCommitMode(yes) {
        if (this._deferSave && !yes) {
            if (this.WFSTLayer !== null)
                if (this._changed) {
                    this._changed = false;
                    this.WFSTLayer.save()
                }
        }
        this._deferSave = yes
    }

    _initMarker(leafletLayer) {
        this.UpdateMarkerTooltip(leafletLayer)

        /*
        let markerOptions = {
            riseOnHover: true,
            autoPan: true,
        };
        */
    }

    /*
    _updateGeometryZoomVisibility(l) {
        //vectorLayer was removed, but leaflet layer event handler is still exists
        if (this.map == null) {
            return false
        }
        let geometryMinZoom = this.workspace.getMinZoom() + 2;

        if (this.map._zoom > geometryMinZoom && this.workspace.visible) {
            l.hiddenByZoomlevel = false;
            if (l._map == null) {
                l.addTo(this.map);
                this._gdebug(l, "has been added to the map")
            }
        } else {
            l.hiddenByZoomlevel = true; // this must be in front of l.removeFrom in order the layer remove handler works correctly
            if (l._map != null) {
                l.removeFrom(this.map)
                this._gdebug(l, "has been removed from the map")
            }
        }
        return true
    }
     */

    UpdateMarkerTooltip(leafletLayer) {

        let content = leafletLayer.getProperty("descr");
        if (content === undefined)
            content = ""

        if (leafletLayer.options.display !== undefined && ! leafletLayer.options.display.label) {
            content = ""
        }

        if (content === "") {
            if (leafletLayer._tooltip) {
                leafletLayer.unbindTooltip()
            }
        } else {
            if (leafletLayer._tooltip) {
                leafletLayer.setTooltipContent(content);
            } else {
                leafletLayer.bindTooltip(content, this.tooltipConfiguration)
            }
        }
    }

    _sendNotification(operation, layers) {
        let names = [];
        let op = "";
        if (operation === CN_GEOMETRY_OP_CREATE) {
            op = "saved"
        } else if (operation === CN_GEOMETRY_OP_UPDATE) {
            op = "updated"
        } else if (operation === CN_GEOMETRY_OP_DELETE) {
            op = "deleted"
        }
        for (let i in layers) {
            names.push(layers[i].feature.id);
            this.elog("DEBUG", `WFST: Geometry (${layers[i].feature.id}) ${op}`);
        }
        mapApi.apiNoticeGeometry(this.workspace, this.id, operation, names ).catch(function (e) {
            this.elog("ERROR", "can't send geometry notification", e)
        }.bind(this))
    }

    setStyle(leafletLayer, styles) {
        leafletLayer.setStyle(styles)
    }

    _initStyle(leafletlayer) {
        try {
            let options = JSON.parse(leafletlayer.getProperty('options'))
            if (options.style)
                this.setStyle(leafletlayer, options.style)
        } catch {

        }
    }

    _initLabelsDisplayFeature(leafletlayer) {
        try {
            let options = JSON.parse(leafletlayer.getProperty('options'))
            if (options.display) {
                if (typeof leafletlayer.initDisplayOptions === "function")
                    //geometries display options
                    leafletlayer.initDisplayOptions(options.display)
                if (leafletlayer instanceof L.Marker) {
                    leafletlayer.options["display"] = ("display" in options) ? options.display : {label: true}
                }
            }
        } catch {

        }
    }

    // leaflet labels display options
    _displayOptions(leafletLayer) {
        if (leafletLayer.options.GD !== undefined) {
            return Object.assign({}, leafletLayer.options.GD.display)
        }
        if (leafletLayer instanceof L.Marker) {
            return {label: leafletLayer.options.display !== undefined ? leafletLayer.options.display.label : true}
        }
    }

    //options we can save to the feature "options" property
    _WFSTFeatureOptions(leafletLayer) {
        return {
            style: this._styleOptions(leafletLayer),
            display: this._displayOptions(leafletLayer),
            editLocked: false,
        }
    }

    _extendFeaturesBounds(l) {
        if (!this.featuresCoveredBounds) {
            this.featuresCoveredBounds =
                l.getBounds ?
                    L.latLngBounds(l.getBounds().getSouthEast(), l.getBounds().getNorthWest()) :
                    L.latLngBounds(l.getLatLng(), l.getLatLng())
        } else {
            this.featuresCoveredBounds.extend(l.getBounds ? l.getBounds() : l.getLatLng())
        }
    }

    /**
     * updates featuresCoveredBounds property
     * according to added geometry features (if ls param is defined)
     * or according to all loaded geometry features
     * usually is called internally after geometry features modification
     * @param ls
     */
    updateFeaturesCoveredBounds(ls) {
        if (!ls)
            ls = this.layer.getLayers()

        if (! Array.isArray(ls))
            ls = [ls]

        if (ls.length === 0)
            return

        // layers that are still not initialized (not added to the map)
        // usually on massive async load, when load event comes before the layer is actually added to the map and getBounds throws error
        //
        let layersNI = []

        // calculate layers bounds
        let b = null
        for (let l of ls) {
            if (!b) {
                if (! l._map) {
                    layersNI.push(l)
                    continue
                }
                b = l.getBounds ?
                        L.latLngBounds(l.getBounds().getSouthEast(), l.getBounds().getNorthWest()) :
                        L.latLngBounds(l.getLatLng(), l.getLatLng())
            } else {
                b.extend(l.getBounds ? l.getBounds() : l.getLatLng())
            }
        }

        // if featuresCoveredBounds already defined and covers calculated bounds
        if (this.featuresCoveredBounds && this.featuresCoveredBounds.contains(b))
            return

        if (! this.featuresCoveredBounds) {
            this.featuresCoveredBounds = b
        } else {
            this.featuresCoveredBounds.extend(b)
        }

        if (this.featuresCoveredBounds) {
            this._logdebug(`bounds extended to ${this.featuresCoveredBounds.toBBoxString()}`)
            this.fire("boundschanged")
        }

        // wait for layers is initialized and then updateFeaturesCoveredBounds
        if (layersNI.length > 0) {
            waiter.add( function () {
                let done = 0
                for (let l of layersNI) {
                    if (l._map)
                        done++
                }
                if (done === layersNI.length) {
                    this.updateFeaturesCoveredBounds(layersNI)
                    return true
                } else
                    return false
            }.bind(this), 1000, function () {
                elog("WARN", `${layersNI.length} features is still not initialized in 1000ms for layer ${this.workspace.id}:${this.id}`)
            }.bind(this))
        }
    }

    getBoundsUrl() {
        if (this.featuresCoveredBounds == null)
            return ""
        let sw = this.featuresCoveredBounds.getSouthWest()
        let ne = this.featuresCoveredBounds.getNorthEast()
        return `${sw.lng}%2C${sw.lat}%2C${ne.lng}%2C${ne.lat}`
    }

    _gdebug(l, msg) {
        if (this.debug)
            this.elog("DEBUG", `Geometry ${l._maphubVectorLayer.name}:${l._maphubVectorLayer.id} ${msg}`)
    }
}

export {VectorLayerDrawing};
